import {
  Badge,
  Box,
  Button,
  Flex,
  H3,
  InlineMessage,
  Message,
  Panel,
  ProgressCircle,
  Table,
  Text,
} from '@bigcommerce/big-design';
import { CheckIcon, CloseIcon, OpenInNewIcon } from '@bigcommerce/big-design-icons';
import { useState } from 'react';

import { LocationEditModal } from '@/components/locations';
import type { Location, LocationFormData } from '@/components/locations/types';
import { useLocations } from '@/hooks/useLocations';

// Unassigned locations table columns
const unassignedColumns = [
  {
    header: 'Location Name',
    hash: 'label',
    render: ({ label }: Location) => <Text bold>{label}</Text>,
  },
  {
    header: 'Code',
    hash: 'code',
    render: ({ code }: Location) => <Text color="secondary60">{code}</Text>,
  },
  {
    header: 'Status in BC',
    hash: 'enabled',
    render: ({ enabled }: Location) => (
      <Badge variant={enabled ? 'success' : 'secondary'} label={enabled ? 'Active' : 'Disabled'} />
    ),
  },
];

/**
 * Stores Page
 *
 * Terminology:
 * - "Stores" = BigCommerce Inventory Locations configured for POS
 * - "Unassigned Locations" = BigCommerce Inventory Locations not yet set up for POS
 * - "Devices" = Individual POS terminals assigned to a store
 *
 * Receipt config hierarchy: Merchant defaults → Store overrides → Device overrides
 */
export function LocationsPage() {
  const {
    locations: stores, // Renamed for clarity
    currencies,
    taxZones,
    channels,
    pickupMethods,
    storeChannelId,
    isLoading,
    error,
    isSaving,
    enableLocation: enableStore,
    updateLocation: updateStore,
    disableLocation: disableStore,
    clearError,
    getTaxZoneName,
    getDefaultCurrency,
    getDefaultPickupMethod,
  } = useLocations();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingStore, setEditingStore] = useState<Location | null>(null);

  const handleOpenModal = (store: Location) => {
    setEditingStore(store);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingStore(null);
  };

  const handleSave = async (store: Location, formData: LocationFormData) => {
    const data = {
      ...formData,
      taxZoneId: formData.taxZoneId || undefined,
      pickupMethodId: formData.pickupMethodId || undefined,
    };

    if (store.isConfiguredForPOS) {
      await updateStore(store.id, data);
    } else {
      await enableStore(store.id, data);
    }
  };

  const handleDisable = async (storeId: number) => {
    await disableStore(storeId);
  };

  if (isLoading) {
    return (
      <Box padding="large">
        <Flex justifyContent="center">
          <ProgressCircle size="large" />
        </Flex>
      </Box>
    );
  }

  const posStores = stores.filter(l => l.isConfiguredForPOS);
  const unassignedLocations = stores.filter(l => !l.isConfiguredForPOS && l.enabled);

  // Find stores missing pickup methods
  const storesWithoutPickup = posStores.filter(s => !s.posSettings?.pickupMethodId);
  const hasPickupWarning = storesWithoutPickup.length > 0;

  return (
    <Box>
      {error && (
        <Message
          type="error"
          header="Error"
          messages={[{ text: error }]}
          marginBottom="medium"
          onClose={clearError}
        />
      )}

      {hasPickupWarning && (
        <Message
          type="warning"
          header="Pickup Method Required"
          messages={[{
            text: `${storesWithoutPickup.length} store(s) don't have a pickup method configured: ${storesWithoutPickup.map(s => s.posSettings?.displayName || s.label).join(', ')}. Edit each store to set a pickup method for accurate tax calculation.`,
          }]}
          marginBottom="medium"
        />
      )}

      {/* Info Panel */}
      <Panel marginBottom="medium">
        <H3 marginBottom="small">Physical Store Management</H3>
        <Text>
          Physical stores are retail locations where you sell products in-person. Each store can have multiple POS devices assigned to it.
        </Text>
        <Text marginTop="small" color="secondary60">
          Stores are synced from your BigCommerce Inventory Locations. Currencies and tax zones are also pulled from BigCommerce.
        </Text>
      </Panel>

      {/* Unassigned BigCommerce Locations - Show First */}
      <Box
        backgroundColor="white"
        padding="large"
        borderRadius="normal"
        shadow="raised"
        marginBottom="large"
      >
        <Flex justifyContent="space-between" alignItems="center" marginBottom="medium">
          <H3 marginBottom="none">Unassigned BigCommerce Locations</H3>
          <Button
            variant="secondary"
            iconLeft={<OpenInNewIcon />}
            onClick={() => window.open('https://login.bigcommerce.com/deep-links/manage/settings/shipping/origin', '_blank', 'noopener')}
          >
            Manage in BigCommerce
          </Button>
        </Flex>
        <Text color="secondary60" marginBottom="medium">
          These are inventory locations from BigCommerce that have not been set up for POS yet.
          Enable them to start selling at that location.
        </Text>

        {unassignedLocations.length === 0 ? (
          <InlineMessage
            type="success"
            messages={[{
              text: stores.length === 0
                ? 'No locations found in BigCommerce. Create locations in your BigCommerce admin to get started.'
                : 'All BigCommerce locations are set up for POS.',
            }]}
          />
        ) : (
          <Table
            columns={[
              ...unassignedColumns,
              {
                header: 'Actions',
                hash: 'actions',
                render: (store: Location) => (
                  <Button variant="secondary" iconLeft={<CheckIcon />} onClick={() => handleOpenModal(store)}>
                    Set Up for POS
                  </Button>
                ),
              },
            ]}
            items={unassignedLocations}
            itemName="locations"
            stickyHeader
          />
        )}
      </Box>

      {/* Active POS Locations */}
      <Box
        backgroundColor="white"
        padding="large"
        borderRadius="normal"
        shadow="raised"
      >
        <Flex justifyContent="space-between" alignItems="center" marginBottom="medium">
          <H3 marginBottom="none">Active Physical Stores</H3>
          <Badge variant="success" label={`${posStores.length} active`} />
        </Flex>
        <Text color="secondary60" marginBottom="medium">
          These stores are set up for POS. You can assign devices, configure currencies, and manage receipts.
        </Text>

        {posStores.length === 0 ? (
          <InlineMessage
            type="warning"
            messages={[{ text: 'No stores set up for POS yet. Set up a BigCommerce location above to get started.' }]}
          />
        ) : (
          <Table
            columns={[
              { header: 'Store Name', hash: 'label', render: ({ label, posSettings }: Location) => (
                <Flex alignItems="center" flexGap="0.5rem">
                  <Text bold>{posSettings?.displayName || label}</Text>
                  {posSettings?.pickupMethodId ? (
                    <Badge variant="success" label="Ready" />
                  ) : (
                    <Badge variant="warning" label="Setup needed" />
                  )}
                </Flex>
              )},
              { header: 'Code', hash: 'code', render: ({ code }: Location) => <Text color="secondary60">{code}</Text> },
              { header: 'Currency', hash: 'currency', render: ({ posSettings }: Location) => (
                <Badge variant="secondary" label={posSettings?.currency || '—'} />
              )},
              { header: 'Tax Zone', hash: 'taxZone', render: ({ posSettings }: Location) => (
                <Text color={posSettings?.taxZoneId ? undefined : 'secondary60'}>{getTaxZoneName(posSettings?.taxZoneId)}</Text>
              )},
              { header: 'Devices', hash: 'devices', render: ({ posSettings }: Location) => (
                <Text>{posSettings?.devices.length || 0} assigned</Text>
              )},
              { header: 'Actions', hash: 'actions', render: (store: Location) => (
                <Flex>
                  <Button variant="subtle" onClick={() => handleOpenModal(store)}>
                    Edit {store.posSettings?.displayName || store.label}
                  </Button>
                  <Button variant="subtle" iconLeft={<CloseIcon />} onClick={() => handleDisable(store.id)}>Disable</Button>
                </Flex>
              )},
            ]}
            items={posStores}
            itemName="stores"
            stickyHeader
          />
        )}
      </Box>

      {/* Store Edit Modal */}
      <LocationEditModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        location={editingStore}
        currencies={currencies}
        taxZones={taxZones}
        channels={channels}
        pickupMethods={pickupMethods}
        storeChannelId={storeChannelId}
        defaultCurrency={getDefaultCurrency()}
        defaultPickupMethodId={editingStore ? getDefaultPickupMethod(editingStore.id)?.id || null : null}
        isSaving={isSaving}
        onSave={handleSave}
      />
    </Box>
  );
}
